import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, SERVICE_PATHS } from '@/lib/api-client';
import type { WhatsAppConfig } from '@/types';
import type { WhatsAppConfigFormData } from '@/schemas/whatsapp-config.schema';

const QUERY_KEY = ['whatsapp-configs'] as const;

/**
 * Fetches all WhatsApp configurations.
 */
export function useWhatsAppConfigs() {
  return useQuery<WhatsAppConfig[]>({
    queryKey: QUERY_KEY,
    queryFn: () => apiGet<WhatsAppConfig[]>(`${SERVICE_PATHS.whatsappConfig}/get-all`),
    staleTime: 30_000,
  });
}

/**
 * Creates a new WhatsApp configuration.
 * Invalidates the configs query on success.
 */
export function useCreateWhatsAppConfig() {
  const queryClient = useQueryClient();

  return useMutation<WhatsAppConfig, Error, WhatsAppConfigFormData>({
    mutationFn: (data) =>
      apiPost<WhatsAppConfig>(`${SERVICE_PATHS.whatsappConfig}/create`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Updates an existing WhatsApp configuration by ID.
 * Invalidates the configs query on success.
 */
export function useUpdateWhatsAppConfig() {
  const queryClient = useQueryClient();

  return useMutation<WhatsAppConfig, Error, { id: string; data: WhatsAppConfigFormData }>({
    mutationFn: ({ id, data }) =>
      apiPut<WhatsAppConfig>(`${SERVICE_PATHS.whatsappConfig}/update/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
